// =========================================================================
// IssueMachinePicture — the still of one machine with the alerting component
// called out, as the alert and incident surfaces place it. Fork-owned (zone C).
//
// The demo's `turnStill(f, health, comp)` (line 1828) at its four call sites:
//
// | Demo line | Call site here |
// |---|---|
// | 3134 `alertCard` `.ac-pic`       | `modules/issues/AlertCard.tsx`            (`size="compact"`) |
// | 3157 `incidentStrip` `.mini`      | `modules/issues/IssuesLiveScene.tsx`      (`size="compact"`) |
// | 2283 `alertPage` `.illwrap`       | `modules/issues/AlertsScene.tsx`          (`size="panel"`)   |
// | 2327 `renderIncident` `.illwrap`  | `modules/issues/IncidentsScene.tsx`       (`size="panel"`)   |
//
// ## What draws the picture
//
// Lane L7's `TurntableStill` (`shared/viz/Turntable.tsx`) — the turntable's
// side-elevation frame in the brand livery, with the demo's SVG callouts over
// it — and, for a model with no frames, its own fallback to the 2D
// `MachineIllustration` side drawing. This file adds only what the issue
// surfaces need around it:
//
// - **the highlight** — the register narrowed to the named component(s), the
//   component half of the demo's filter (`IssueMachinePicture.transform.ts`),
//   so only that component is called out;
// - **the placement** — `compact` (card cells and strips, capped at 118 px
//   high, the demo's `.inccard .mini`) or `panel` (a detail page's side panel);
// - **one accessible image** — `role="img"` with a name that says which
//   component is highlighted and its score; the frame inside is presentational.
//
// It is not interactive: each call site wraps it in its own link to the
// machine page (the demo's `.go` tiles) and prints the component, score and
// band in a caption under it.
//
// Not to be confused with L7's `MachineThumb` (the demo's `turnIcon`), the
// small icon on machine cards.
//
// UI layer — renders output. Lookups go through `data/core` and the engine;
// the register narrowing and the accessible name come from the sibling
// transform.
// =========================================================================

import { BY_ID } from '../../data/core';
import { cn } from '@/shared/cn';
import { healthOf } from '../oem-engine';
import {
  highlightList,
  thumbAriaLabel,
  thumbHealth,
  type ThumbHighlight,
} from './IssueMachinePicture.transform';
import { TurntableStill } from './Turntable';

/** `compact` — card cells and strips; `panel` — the side panel of a detail page. */
export type IssueMachinePictureSize = 'compact' | 'panel';

export interface IssueMachinePictureProps {
  /** Fleet id (`BY_ID` key). An unknown id renders nothing. */
  readonly machineId: string;
  /**
   * The alerting / failed component(s) to call out. `null` or empty calls out
   * every flagged component, as the demo does when `comp` is `null`.
   */
  readonly highlight?: ThumbHighlight;
  /** Default `compact`. */
  readonly size?: IssueMachinePictureSize;
  readonly className?: string;
}

/** Width cap per size; the still keeps its own 16:9 frame. `.mini` caps at 118 px high. */
const FRAME: Readonly<Record<IssueMachinePictureSize, string>> = {
  compact: 'mx-auto w-full max-w-[210px]',
  panel: 'w-full',
};

export function IssueMachinePicture({
  machineId,
  highlight,
  size = 'compact',
  className,
}: IssueMachinePictureProps) {
  const machine = BY_ID[machineId];
  if (!machine) return null;

  const wanted = highlightList(highlight);
  const register = healthOf(machineId);
  const rows = thumbHealth(register, wanted);
  const label = thumbAriaLabel(machineId, machine.model, wanted, register);

  return (
    <div
      role="img"
      aria-label={label}
      title={label}
      data-machine-picture={machineId}
      className={cn(FRAME[size], className)}
    >
      <TurntableStill
        machine={machine}
        health={rows}
        component={wanted.length === 1 ? wanted[0] : null}
      />
    </div>
  );
}
